import type { CotacaoState } from '../store/cotacaoSlice';

/**
 * O que ainda falta na **etapa 1** do fluxo de cotação (at-62, D1).
 *
 * 🔴 Existe por causa de um defeito que Victor descreveu em 10/09/2026: estando
 * em `/cotacao-produtos`, a bolinha "Dados" ficava **verde** só por ser a etapa
 * anterior — *"o que dá a entender que os dados estão prontos, o que nem sempre
 * é verdade"*. O indicador afirmava algo que ninguém tinha verificado.
 *
 * Esta função é o que faz a afirmação ser verificada: devolve os campos que
 * faltam, em **nome de usuário** (não o nome da chave), prontos para a tela
 * listar. Lista vazia = etapa 1 completa.
 *
 * ⚠️ **Função pura**, sem rede e sem store — recebe o estado e devolve texto. É
 * consumida pelo `Stepper` (bolinha vermelha) e pela faixa de aviso da etapa 2.
 *
 * ⚠️ **Não bloqueia nada.** O consultor pode tentar gerar mesmo assim e receber
 * o erro do backend — mesma escolha da checagem de CPF da at-58 (D3): avisar sem
 * impedir, porque uma regra local mais estrita que a do servidor barraria
 * cotação legítima. O que ela evita é a tela **mentir** dizendo que está pronto.
 *
 * 📌 A ordem é a do formulário, para a lista ler como um caminho de correção.
 */
export function dadosDaCotacaoPendentes(cotacao: CotacaoState | null | undefined): string[] {
  if (!cotacao) return ['dados do cliente'];

  const faltando: string[] = [];
  const preenchido = (valor?: string | null) => Boolean((valor ?? '').trim());
  const temDigitos = (valor?: string | null) => (valor ?? '').replace(/\D/g, '').length > 0;

  if (!preenchido(cotacao.nome)) faltando.push('nome');
  // Obrigatório desde a at-62 (P1): `GerarCotacaoPdfDto.cpfAssociado` é
  // `@IsNotEmpty` e o backend localiza o lead pelo CPF.
  if (!temDigitos(cotacao.cpf)) faltando.push('CPF');
  if (!preenchido(cotacao.email)) faltando.push('e-mail');
  if (!temDigitos(cotacao.celular)) faltando.push('celular');
  if (!preenchido(cotacao.placa)) faltando.push('placa');
  if (!preenchido(cotacao.ano)) faltando.push('ano do modelo');
  if (!preenchido(cotacao.marca)) faltando.push('marca');
  if (!preenchido(cotacao.modelo)) faltando.push('modelo');
  if (!preenchido(cotacao.valorFipe)) faltando.push('valor FIPE');
  if (!preenchido(cotacao.tipoVeiculo)) faltando.push('tipo de veículo');
  if (!preenchido(cotacao.codigoRegionalSelecionada)) faltando.push('regional');
  if (!preenchido(cotacao.codigoCooperativaSelecionada)) faltando.push('cooperativa');
  if (!preenchido(cotacao.cotaId)) faltando.push('cota');

  return faltando;
}
